import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "./components/ui/card";
import { 
  Heart, 
  MapPin, 
  Check, 
  Menu, 
  X, 
  ChevronDown,
  Calendar,
  Users,
  Sparkles,
  Phone as PhoneIcon,
  Video,
  Globe,
  MessageCircle,
  Gift,
  ArrowRight
} from "lucide-react";
import { Toaster } from "sonner";
import ServiceDetailPage from "./pages/ServiceDetail";
import AboutPage from "./pages/AboutPage";
import GiftPage from "./pages/GiftPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BOOKING_URL = "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2sLR40fV8O-hfoDyzVRQq1wTbDNf0T-8Ovy79yBcvaGSRyMCmuzMMj_fEnqs6AjhDQlKyeVbLd";
const MESSAGE_URL = "https://trina-cobbler.clientsecure.me/contact-widget";
const PORTAL_URL = "https://trina-cobbler.clientsecure.me/";

const LOGO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_1e730d40-55a8-4421-8dbe-a02d71b76db0/artifacts/xa7bhn0s_People%27s%20medicine%20logo%202.png";
const HERO_LOGO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_1e730d40-55a8-4421-8dbe-a02d71b76db0/artifacts/dh3ogrhk_People%27s%20Medicine%20Logo%201.webp";
const FOOTER_LOGO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_1e730d40-55a8-4421-8dbe-a02d71b76db0/artifacts/xa7bhn0s_People%27s%20medicine%20logo%202.png";

const SERVICE_LINKS = [
  { id: "doula-direct", name: "Doula Direct™" },
  { id: "10-visit-package", name: "10-Visit Support Package" },
  { id: "birth-doula", name: "Labor Doula Services" },
  { id: "postpartum-doula", name: "Postpartum Doula Services" },
  { id: "childbirth-education", name: "Childbirth Education" },
  { id: "family-support", name: "Family Support Services" },
];

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
  }, [location]);

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHomePage ? 'bg-white/95 backdrop-blur-md shadow-xl' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo + Portal */}
          <div className="flex flex-col items-start">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src={LOGO_URL} alt="People's Medicine" className="h-14 md:h-18 w-auto" />
            </Link>
            <a 
              href={PORTAL_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="portal-signin-btn"
              className="font-body text-xs text-teal hover:text-teal-dark font-semibold border border-teal/40 rounded-full px-3 py-0.5 hover:bg-teal/10 transition-all duration-300 ml-1 -mt-1"
            >
              Portal Sign In
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <Link
              to="/about"
              data-testid="nav-about"
              className="font-body text-sm text-plum hover:text-teal transition-colors duration-300"
            >
              About
            </Link>
            
            {/* Services Dropdown */}
            <div className="relative">
              <button
                data-testid="nav-services-dropdown"
                className="flex items-center gap-1 font-body text-sm text-plum hover:text-teal transition-colors duration-300"
                onClick={() => setServicesOpen(!servicesOpen)}
                onMouseEnter={() => setServicesOpen(true)}
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {servicesOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border-2 border-peach py-2 animate-fade-in"
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <a
                    href={isHomePage ? "#services" : "/#services"}
                    className="block px-4 py-2 font-body text-teal hover:bg-peach/20 transition-colors font-semibold text-sm"
                  >
                    All Services Overview
                  </a>
                  <div className="border-t border-peach my-2"></div>
                  {SERVICE_LINKS.map((service) => (
                    <Link
                      key={service.id}
                      to={`/services/${service.id}`}
                      data-testid={`nav-service-${service.id}`}
                      className="block px-4 py-2 font-body text-sm text-plum hover:bg-peach/20 hover:text-teal transition-colors"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href={isHomePage ? "#contact" : "/#contact"}
              data-testid="nav-contact"
              className="font-body text-sm text-plum hover:text-teal transition-colors duration-300"
            >
              Contact
            </a>

            <Link
              to="/gift"
              data-testid="nav-gift"
              className="font-body text-sm text-plum hover:text-teal transition-colors duration-300 flex items-center gap-1"
            >
              <Gift className="w-3.5 h-3.5" />
              Gift for Her
            </Link>

            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="nav-schedule-btn"
                className="bg-teal hover:bg-teal-dark text-white rounded-full px-5 text-sm transform hover:-translate-y-0.5 transition-all duration-300"
              >
                <Calendar className="w-4 h-4 mr-1.5" />
                Schedule a Support Call
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden text-plum"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-md rounded-2xl mt-2 p-6 shadow-xl animate-fade-in max-h-[80vh] overflow-y-auto border-2 border-peach">
            <Link to="/about" className="block py-3 font-body text-plum hover:text-teal" onClick={() => setIsOpen(false)}>About</Link>
            
            <div className="py-3">
              <button className="flex items-center justify-between w-full font-body text-plum" onClick={() => setServicesOpen(!servicesOpen)}>
                <span>Services</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="mt-2 ml-4 space-y-2">
                  <a href={isHomePage ? "#services" : "/#services"} className="block py-2 font-body text-teal text-sm" onClick={() => setIsOpen(false)}>All Services Overview</a>
                  {SERVICE_LINKS.map((service) => (
                    <Link key={service.id} to={`/services/${service.id}`} className="block py-2 font-body text-plum/80 text-sm hover:text-teal" onClick={() => setIsOpen(false)}>{service.name}</Link>
                  ))}
                </div>
              )}
            </div>

            <a href={isHomePage ? "#contact" : "/#contact"} className="block py-3 font-body text-plum hover:text-teal" onClick={() => setIsOpen(false)}>Contact</a>
            <Link to="/gift" className="block py-3 font-body text-plum hover:text-teal flex items-center gap-1.5" onClick={() => setIsOpen(false)}><Gift className="w-4 h-4" /> Gift for Her</Link>
            
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block mt-4">
              <Button data-testid="mobile-schedule-btn" className="w-full bg-teal hover:bg-teal-dark text-white rounded-full">
                <Calendar className="w-4 h-4 mr-2" />
                Schedule a Support Call
              </Button>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Sticky Mobile Book Button
const StickyMobileButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-40 lg:hidden">
      <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
        <Button 
          data-testid="sticky-book-btn"
          className="w-full bg-teal hover:bg-teal-dark text-white rounded-full py-4 text-base shadow-2xl"
        >
          <Calendar className="w-5 h-5 mr-2" />
          Book Now
        </Button>
      </a>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="home" 
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center pt-20 md:pt-24"
      style={{ background: `linear-gradient(135deg, #FDFBF7 0%, #F5D5C8 50%, #F4C4A8 100%)` }}
    >
      <div className="flex justify-center mb-4 md:mb-6 animate-fade-in px-4">
        <img src={HERO_LOGO_URL} alt="People's Medicine" className="h-36 sm:h-48 md:h-60 lg:h-72 w-auto" />
      </div>

      <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-12 py-6 sm:py-8 md:py-12 text-center flex-1 flex flex-col justify-center">
        <div className="space-y-6 md:space-y-8 animate-fade-in-up">
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-semibold text-plum leading-[1.1] tracking-tight">
            You've Read the Books. Now Experience Real Support.
          </h1>
          <p className="font-body text-base md:text-lg lg:text-xl text-plum/80 leading-relaxed max-w-2xl mx-auto">
            You've done the research. Now let someone walk beside you. From pregnancy through postpartum, People's Medicine provides personalized, trauma-informed, and compassionate care to help you navigate every stage of your journey with confidence, clarity, and peace of mind.
          </p>

          {/* Delivery Method Badges */}
          <div className="flex items-center justify-center gap-3 sm:gap-6" data-testid="delivery-badges">
            <span className="flex items-center gap-1.5 font-body text-sm md:text-base text-teal font-semibold">
              <Video className="w-4 h-4" /> Virtual
            </span>
            <span className="w-1 h-1 rounded-full bg-plum/40"></span>
            <span className="flex items-center gap-1.5 font-body text-sm md:text-base text-teal font-semibold">
              <PhoneIcon className="w-4 h-4" /> Phone
            </span>
            <span className="w-1 h-1 rounded-full bg-plum/40"></span>
            <span className="flex items-center gap-1.5 font-body text-sm md:text-base text-teal font-semibold">
              <MapPin className="w-4 h-4" /> In-Person
            </span>
          </div>

          {/* Doula On Call Indicator */}
          <Link to="/services/doula-direct" className="inline-flex items-center gap-2.5 bg-white/80 backdrop-blur-sm rounded-full px-5 py-2.5 shadow-md border border-teal/20 hover:shadow-lg transition-all duration-300 group" data-testid="doula-on-call">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
            <span className="font-body text-sm font-semibold text-plum group-hover:text-teal transition-colors">
              Doula Direct™
            </span>
            <span className="font-body text-xs text-plum/60">
              Doula on Call Daily 9am-9pm EST
            </span>
          </Link>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2 md:pt-4">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button 
                data-testid="hero-schedule-btn"
                size="lg"
                className="w-full sm:w-auto bg-teal hover:bg-teal-dark text-white rounded-full px-8 py-5 sm:py-6 text-base sm:text-lg transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule a Support Call
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 scroll-indicator">
        <ChevronDown className="w-6 h-6 md:w-8 md:h-8 text-teal" />
      </div>
    </section>
  );
};

// Why Trina Cares - Emotional Story Section
const WhySection = () => (
  <section data-testid="why-section" className="py-14 md:py-24 lg:py-32 bg-white">
    <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-12">
      <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        {/* Image */}
        <div className="flex justify-center">
          <div className="rounded-3xl overflow-hidden shadow-xl max-w-sm md:max-w-md">
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_1e730d40-55a8-4421-8dbe-a02d71b76db0/artifacts/5r56zdci_Trina%20Cobbler%20People%27s%20Medicine%20Wellness.webp"
              alt="Trina Cobbler"
              className="w-full h-auto object-cover object-top"
            />
          </div>
        </div>

        {/* Story */}
        <div className="space-y-6">
          <p className="text-xs sm:text-sm uppercase tracking-widest text-teal font-body font-bold">
            Why I Do This
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-plum leading-tight">
            Because No One Should Feel Alone During Life's Most Sacred Transitions
          </h2>
          <div className="space-y-4 font-body text-plum/80 leading-relaxed">
            <p>
              I became a doula because I saw what happens when women don't have support. The confusion. The isolation. The quiet moments where you wonder if what you're feeling is normal and have no one to ask.
            </p>
            <p>
              That's not how this journey should feel. Pregnancy, birth, and postpartum are some of life's most sacred transitions. You deserve someone who listens without judgment, who shows up when things get real, and who reminds you that what you're going through matters.
            </p>
            <p className="font-semibold text-plum">
              I'm here because I care deeply about you, your baby, and your family. Not just on the big day, but in the quiet in-between moments where support makes all the difference.
            </p>
          </div>
          <div className="pt-2">
            <Link to="/about">
              <Button 
                variant="outline"
                className="rounded-full px-6 py-5 border-2 border-plum/30 text-plum hover:bg-plum/10 transition-all duration-300"
              >
                More About Trina
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section (No Pricing - Connection-Focused)
const ServicesSection = () => {
  const services = [
    {
      title: "Doula Direct™",
      description: "Have a question at 2 AM? Feeling overwhelmed and need someone who gets it? Doula Direct™ gives you real-time access to compassionate doula support whenever you need it.",
      cta: "Learn More",
      link: "/services/doula-direct",
      icon: <PhoneIcon className="w-7 h-7" />
    },
    {
      title: "Labor Doula Support",
      description: "You deserve an advocate who stays by your side from the first contraction to the first breath. I'm there for the whole journey so you never face it alone.",
      cta: "Learn More",
      link: "/services/birth-doula",
      icon: <Heart className="w-7 h-7" />
    },
    {
      title: "Postpartum Care",
      description: "The baby is here and so is a whole new world of emotions, questions, and exhaustion. Let me help you navigate recovery, feeding, and finding your rhythm.",
      cta: "Learn More",
      link: "/services/postpartum-doula",
      icon: <Sparkles className="w-7 h-7" />
    },
    {
      title: "10-Visit Support Package",
      description: "Consistent, personalized visits across pregnancy and postpartum because real support isn't a one-time thing. It's someone who knows your story showing up again and again.",
      cta: "Learn More",
      link: "/services/10-visit-package",
      icon: <Users className="w-7 h-7" />
    },
    {
      title: "Childbirth Education",
      description: "Knowledge replaces fear. From healthy pregnancy practices to newborn care essentials feel prepared, informed, and confident in every decision you make.",
      cta: "Learn More",
      link: "/services/childbirth-education",
      icon: <Globe className="w-7 h-7" />
    },
    {
      title: "Family Support Services",
      description: "Your whole family is changing not just you. From sibling care during birth to healthy meal prep, we support everyone under your roof through this beautiful transition.",
      cta: "Learn More",
      link: "/services/family-support",
      icon: <Gift className="w-7 h-7" />
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="py-12 md:py-20 lg:py-32 bg-gradient-to-br from-peach/20 to-clay/20">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-widest text-teal font-body font-bold mb-3 md:mb-4">
            How I Can Support You
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-plum mb-4 md:mb-6">
            Every Family's Journey Is Different. Your Support Should Be Too.
          </h2>
          <p className="font-body text-base md:text-lg text-plum/70 leading-relaxed">
            Whether you need someone to answer a late-night question, hold your hand during labor, or help you find your footing as a new parent I'm here.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {services.map((service, index) => (
            <Card 
              key={index}
              data-testid={`service-card-${index}`}
              className="rounded-3xl border-2 border-peach/50 bg-white overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-teal/30 group"
            >
              <CardHeader className="pb-2">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 bg-teal/10 text-teal group-hover:bg-teal group-hover:text-white transition-all duration-300">
                  {service.icon}
                </div>
                <CardTitle className="font-heading text-xl md:text-2xl text-plum">
                  {service.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-body text-sm text-plum/70 leading-relaxed">
                  {service.description}
                </p>
              </CardContent>
              <CardFooter>
                <Link to={service.link} className="w-full">
                  <Button 
                    data-testid={`service-cta-${index}`}
                    className="w-full rounded-full bg-plum/10 hover:bg-teal hover:text-white text-plum transition-all duration-300"
                  >
                    {service.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Brand Promise Section
const BrandPromise = () => (
  <section data-testid="brand-promise" className="py-14 md:py-20 bg-teal text-white text-center">
    <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12">
      <Heart className="w-10 h-10 mx-auto mb-6 opacity-70" />
      <p className="font-heading text-2xl sm:text-3xl md:text-4xl italic leading-relaxed mb-4">
        "Support changes the entire journey."
      </p>
      <p className="font-body text-white/80 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
        From the first flutter of uncertainty to the first time you hold your baby I walk with you. Not in front. Not behind. Right beside you.
      </p>
      <div className="mt-8">
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
          <Button className="bg-white text-teal hover:bg-peach rounded-full px-8 py-5 text-base sm:text-lg transition-all duration-300">
            <Calendar className="w-5 h-5 mr-2" />
            Let's Talk
          </Button>
        </a>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is Doula Direct™?",
      answer: "Doula Direct™ is our signature service that gives you immediate access to professional doula support via Zoom or phone. Whether you have a quick question or need in-depth guidance on pregnancy, birth prep, postpartum, or newborn care you can book a session starting at $75 with no long-term commitment required."
    },
    {
      question: "Do you offer virtual support?",
      answer: "Yes! Virtual and phone support is available nationwide. You can connect from anywhere in the country via Zoom or phone. In-person services are available throughout all 23 counties of Maryland, the DMV area, and we're willing to travel contact us for details."
    },
    {
      question: "How quickly can I book?",
      answer: "You can book a session directly through our online scheduling system often with same-week availability. For Doula Direct™ Monthly subscribers, priority scheduling is included so you can get support when you need it most."
    },
    {
      question: "Is this covered by insurance?",
      answer: "We accept CareFirst BlueCross BlueShield (Commercial & Community) and UnitedHealthcare (Commercial & Community). Please contact us to verify your specific plan coverage."
    },
    {
      question: "What areas do you serve?",
      answer: "We serve all 23 counties of Maryland, the DMV (DC, Maryland, Virginia) area, and are willing to travel for in-person services. Virtual and phone support is available to families across America. Contact us for more info about availability in your location."
    },
    {
      question: "What's the difference between a doula and a midwife?",
      answer: "A midwife is a medical professional who can deliver babies, perform exams, and prescribe medications. A doula provides non-medical support emotional encouragement, physical comfort measures, advocacy, and education. Many families choose to have both for comprehensive care."
    }
  ];

  return (
    <section data-testid="faq-section" className="py-12 md:py-20 lg:py-28 bg-gradient-to-br from-peach/20 to-clay/20">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xs sm:text-sm uppercase tracking-widest text-teal font-body font-bold mb-3 md:mb-4">FAQ</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-plum mb-4 md:mb-6">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-3 md:space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} data-testid={`faq-item-${index}`} className="bg-white rounded-2xl border border-peach/50 shadow-sm overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full flex items-center justify-between p-5 md:p-6 text-left">
                <span className="font-body font-semibold text-plum text-sm md:text-base pr-4">{faq.question}</span>
                <ChevronDown className={`w-5 h-5 text-teal shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === index && (
                <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1">
                  <p className="font-body text-sm md:text-base text-plum/75 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Insurance Section
const InsuranceSection = () => {
  return (
    <section data-testid="insurance-section" className="py-12 md:py-16 bg-white">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-12 text-center">
        <p className="text-xs sm:text-sm uppercase tracking-widest text-teal font-body font-bold mb-3 md:mb-4">Insurance Accepted</p>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium text-plum mb-8 md:mb-12">
          We Accept the Following Insurance
        </h2>
        <div className="grid sm:grid-cols-2 gap-6 md:gap-8 max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 flex flex-col items-center gap-4">
            <img src="https://customer-assets.emergentagent.com/job_1e730d40-55a8-4421-8dbe-a02d71b76db0/artifacts/mzqlb0ii_CareFirst.webp" alt="CareFirst BlueCross BlueShield" className="h-40 md:h-44 w-auto object-contain" />
            <div className="space-y-1">
              <p className="font-body text-sm font-semibold text-plum">Commercial</p>
              <p className="font-body text-sm font-semibold text-plum">Community</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 flex flex-col items-center gap-4">
            <img src="https://customer-assets.emergentagent.com/job_1e730d40-55a8-4421-8dbe-a02d71b76db0/artifacts/ln1n2ya4_lg-67f321d080ed1-United-HealthCare.webp" alt="UnitedHealthcare" className="h-40 md:h-44 w-auto object-contain" />
            <div className="space-y-1">
              <p className="font-body text-sm font-semibold text-plum">Commercial</p>
              <p className="font-body text-sm font-semibold text-plum">Community</p>
            </div>
          </div>
        </div>
        <p className="font-body text-sm text-plum/60 mt-8">Please contact us to verify your specific plan coverage.</p>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" data-testid="contact-section" className="py-12 md:py-20 bg-gradient-to-br from-peach/40 to-clay/40">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="text-center mb-8 md:mb-12">
          <p className="text-xs sm:text-sm uppercase tracking-widest text-teal font-body font-bold mb-3 md:mb-4">Get In Touch</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-plum mb-4 md:mb-6">Let's Start a Conversation</h2>
          <p className="font-body text-base md:text-lg text-plum/70 leading-relaxed max-w-2xl mx-auto">
            Whether you need pregnancy guidance, postpartum support, newborn care tips, or just want to connect we're here for you.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
          <div className="bg-white rounded-2xl p-6 text-center shadow-xl border-2 border-teal/20">
            <div className="w-12 h-12 bg-teal rounded-2xl flex items-center justify-center mx-auto mb-4">
              <PhoneIcon className="w-6 h-6 text-white" />
            </div>
            <p className="font-body font-semibold text-plum mb-1">Call Us</p>
            <a href="tel:+18565675907" className="font-body text-teal hover:underline text-sm">(856) 567-5907</a>
          </div>
          <div className="bg-white rounded-2xl p-6 text-center shadow-xl border-2 border-teal/20">
            <div className="w-12 h-12 bg-teal rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <p className="font-body font-semibold text-plum mb-1">Message Us</p>
            <a href={MESSAGE_URL} target="_blank" rel="noopener noreferrer" className="font-body text-teal hover:underline text-sm">Client Portal</a>
          </div>
          <div className="bg-white rounded-2xl p-6 text-center shadow-xl border-2 border-teal/20">
            <div className="w-12 h-12 bg-teal rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <p className="font-body font-semibold text-plum mb-1">Service Area</p>
            <p className="font-body text-plum/70 text-sm">Nationwide virtual & phone. In-person across MD, DC & VA.</p>
          </div>
        </div>

        <div className="bg-teal rounded-3xl p-6 sm:p-8 md:p-12 text-white text-center">
          <h3 className="font-heading text-2xl sm:text-3xl font-medium mb-6 md:mb-8">Ready to Get Started?</h3>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button data-testid="contact-book-btn" size="lg" className="w-full sm:w-auto bg-white text-teal hover:bg-peach rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg transition-all duration-300">
              <Calendar className="w-5 h-5 mr-2" />
              Schedule a Support Call
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-plum text-white py-10 md:py-16">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src={FOOTER_LOGO_URL} alt="People's Medicine" className="h-20 w-auto" />
            </Link>
            <p className="font-body text-white/70 leading-relaxed">
              Compassionate doula support for families navigating pregnancy, birth, postpartum, and newborn care. Virtual, phone, and in-person support changes the entire journey.
            </p>
          </div>
          <div>
            <h4 className="font-heading text-lg font-medium mb-4">Quick Links</h4>
            <div className="space-y-2">
              <Link to="/" className="block font-body text-white/70 hover:text-peach transition-colors">Home</Link>
              <Link to="/about" className="block font-body text-white/70 hover:text-peach transition-colors">About Trina</Link>
              <a href="/#services" className="block font-body text-white/70 hover:text-peach transition-colors">Services & Pricing</a>
              <a href="/#contact" className="block font-body text-white/70 hover:text-peach transition-colors">Contact</a>
              <Link to="/gift" className="block font-body text-white/70 hover:text-peach transition-colors">Gift for Her</Link>
            </div>
          </div>
          <div>
            <h4 className="font-heading text-lg font-medium mb-4">Services</h4>
            <div className="space-y-2">
              {SERVICE_LINKS.map((service) => (
                <Link key={service.id} to={`/services/${service.id}`} className="block font-body text-white/70 hover:text-peach transition-colors">{service.name}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-heading text-lg font-medium mb-4">Service Area</h4>
            <a 
              href="https://www.google.com/maps/place/People's+Medicine+Wellness/@39.0428548,-76.9453619,17z/data=!3m1!4b1!4m6!3m5!1s0x89b7c91f2ef5f22f:0xc444d0e18af489e3!8m2!3d39.0428548!4d-76.9453619!16s%2Fg%2F11w830_vkm?entry=ttu&g_ep=EgoyMDI2MDgxNi4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl overflow-hidden hover:opacity-90 transition-opacity"
            >
              <iframe
                title="People's Medicine Wellness Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3107.5!2d-76.9453619!3d39.0428548!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7c91f2ef5f22f%3A0xc444d0e18af489e3!2sPeople&#39;s%20Medicine%20Wellness!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="180"
                style={{border: 0, borderRadius: '12px'}}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="pointer-events-none"
              ></iframe>
            </a>
            <p className="font-body text-white/70 text-sm mt-2">Beltsville, MD</p>
          </div>
        </div>
        <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-body text-white/60 text-sm">© {new Date().getFullYear()} People's Medicine. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href="/terms" className="font-body text-white/60 text-sm hover:text-peach transition-colors">Terms of Service</a>
            <span className="text-white/30">|</span>
            <a href="/privacy" className="font-body text-white/60 text-sm hover:text-peach transition-colors">Privacy Policy</a>
          </div>
          <div className="flex flex-col items-center gap-1">
            <p className="font-body text-white/60 text-sm">Located in: Beltsville, MD</p>
            <p className="font-body text-white/60 text-sm">Virtual & Phone Nationwide · In-Person MD, DC & VA</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const HomePage = () => {
  return (
    <>
      <HeroSection />
      <WhySection />
      <ServicesSection />
      <BrandPromise />
      <ContactSection />
      <FAQSection />
      <InsuranceSection />
    </>
  );
};

// Main App
function App() {
  return (
    <div className="App bg-warm-white min-h-screen">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />
        <StickyMobileButton />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/gift" element={<GiftPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
